import React from 'react';
import {
    List,
    Datagrid,
    TextField,
    DateField,
    Edit,
    SimpleForm,
    TextInput,
    Create,
    required,
    Show,
    SimpleShowLayout,
    ReferenceManyField,
    FunctionField,
    usePermissions,
    EditButton,
    DeleteButton,
    ShowButton,
    CreateButton,
    TopToolbar,
    Button,
    useRecordContext,
    useNotify,
    useRefresh,
    Pagination,
} from 'react-admin';
import DescriptionIcon from '@mui/icons-material/Description';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { JumpstartTextInput, JumpstartSearchInput, JumpstartFormToolbar } from '../components/JumpstartInputs';
import { useT } from '../i18n/useT';

// Export publications button
const ExportPublicationsButton = () => {
    const notify = useNotify();
    const t = useT();

    const handleExport = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                notify(t('notifications.authRequired'), { type: 'error' });
                return;
            }

            // Fetch all publications
            const response = await fetch('http://localhost:8000/api/v1/publications?skip=0&limit=10000', {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error('Failed to fetch publications');
            }

            const publications = await response.json();

            // Create CSV content
            const csvContent = [
                ['ID', 'SKU', 'Title', 'Description', 'Created At', 'Updated At'],
                ...publications.map((pub: any) => [
                    pub.id,
                    pub.sku,
                    pub.title,
                    pub.description || '',
                    pub.created_at,
                    pub.updated_at,
                ]),
            ]
                .map((row: any[]) => row.map((cell: any) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
                .join('\n');

            // Create and download CSV file
            const blob = new Blob([csvContent], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `publications_${new Date().toISOString().split('T')[0]}.csv`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

            notify(t('resources.publications.notifications.exportSuccess'), { type: 'success' });
        } catch (error) {
            console.error('Export error:', error);
            notify(t('resources.publications.notifications.exportFailed'), { type: 'error' });
        }
    };

    return (
        <Button
            label="actions.exportCsv"
            onClick={handleExport}
            startIcon={<FileDownloadIcon />}
        />
    );
};

// Filter bar – sources match backend query param names
const PublicationFilters = () => {
    const t = useT();
    return [
        <JumpstartSearchInput key="sku"   source="sku"   placeholder={t('resources.publications.filters.skuPlaceholder')}   alwaysOn />,
        <JumpstartSearchInput key="title" source="title" placeholder={t('resources.publications.filters.titlePlaceholder')} alwaysOn />,
    ];
};

const PublicationListActions = () => {
    const { permissions } = usePermissions();
    return (
        <TopToolbar>
            <ExportPublicationsButton />
            {permissions === 'admin' && <CreateButton />}
        </TopToolbar>
    );
};

// List of publications with filtering
export const PublicationList = () => {
    const { permissions } = usePermissions();
    const t = useT();

    return (
        <List
            sort={{ field: 'created_at', order: 'DESC' }}
            filters={PublicationFilters()}
            actions={<PublicationListActions />}
            perPage={25}
            pagination={<Pagination rowsPerPageOptions={[10, 25, 50, 100]} />}
            empty={<div style={{ padding: '1rem', textAlign: 'center' }}>
                <h3>{t('resources.publications.empty.title')}</h3>
                <p>{t('resources.publications.empty.hint')}</p>
            </div>}
        >
            <Datagrid rowClick={false}>
                <TextField source="id" />
                <TextField source="sku" />
                <TextField source="title" />
                <TextField source="description" />
                <DateField source="created_at" showTime />
                <DateField source="updated_at" showTime />
                <ShowButton />
                {permissions === 'admin' && (
                    <>
                        <EditButton />
                        <DeleteButton />
                    </>
                )}
            </Datagrid>
        </List>
    );
};

// Show publication details
export const PublicationShow = () => {
    const { permissions } = usePermissions();
    const t = useT();
    return (
        <Show>
            <SimpleShowLayout>
                <TextField source="id" />
                <TextField source="sku" />
                <TextField source="title" />
                <TextField source="description" />
                <DateField source="created_at" showTime />
                <DateField source="updated_at" showTime />
                
                {/* Show related files */}
                <ReferenceManyField
                    label={t('resources.publications.relatedFilesLabel')}
                    reference="files"
                    target="publication_id"
                >
                    <Datagrid>
                        <TextField source="id" />
                        <TextField source="file_type" />
                        <TextField source="filename" />
                        <FunctionField
                            source="size"
                            render={(record: any) => {
                                if (!record.size) return 'N/A';
                                const mb = (record.size / 1024 / 1024).toFixed(2);
                                return `${mb} MB`;
                            }}
                        />
                        <DateField source="created_at" showTime />
                        <ShowButton />
                    </Datagrid>
                </ReferenceManyField>
            </SimpleShowLayout>
        </Show>
    );
};

// Edit publication
export const PublicationEdit = () => {
    return (
        <Edit>
            <SimpleForm toolbar={<JumpstartFormToolbar />}>
                <JumpstartTextInput source="sku" validate={[required()]} disabled />
                <JumpstartTextInput source="title" validate={[required()]} fullWidth />
                <JumpstartTextInput source="description" multiline rows={4} fullWidth />
            </SimpleForm>
        </Edit>
    );
};

// Create new publication
export const PublicationCreate = () => {
    const t = useT();
    return (
        <Create>
            <SimpleForm toolbar={<JumpstartFormToolbar />}>
                <JumpstartTextInput source="sku" validate={[required()]} helperText={t('resources.publications.helpers.skuUnique')} />
                <JumpstartTextInput source="title" validate={[required()]} fullWidth />
                <JumpstartTextInput source="description" multiline rows={4} fullWidth />
            </SimpleForm>
        </Create>
    );
};

export const publicationsIcon = DescriptionIcon;
